import { apiClient } from "shared/api/client/orval";
import { validateVRCode } from "shared/api/client/orvalHelpers";

/**
 * Pair a VR headset by validating the 6-digit code displayed inside it.
 *
 * Thin wrapper around the shared Orval helper so feature code does not depend
 * directly on `shared/api/client/orvalHelpers` import paths.
 */
export async function pairHeadsetWithCode(code: string): Promise<unknown> {
  return validateVRCode(code);
}

export type HeadsetPairingCode = {
  sessionId: string;
  code: string;
  expiresAt: string | null;
};

/**
 * Mint a fresh 6-digit pairing code, exactly as a headset would on its login
 * screen. Lets operators verify the pairing flow end-to-end without a device.
 */
export async function generateHeadsetPairingCode(): Promise<HeadsetPairingCode> {
  const res = await apiClient.post("/api/vr-auth/generate-code");
  const o = asRec(res.data);
  return {
    sessionId: str(pick(o, "sessionId", "SessionId")),
    code: str(pick(o, "code", "Code")),
    expiresAt: optStr(pick(o, "expiresAt", "ExpiresAt")),
  };
}

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

export type RegisteredDevice = {
  deviceId: string;
  platform: string;
  publicKey?: string;
  registeredAt: string;
};

export type RegisterDevicePayload = {
  deviceId: string;
  platform: string;
  publicKey: string;
  appVersion: string;
};

export type VenuePlaylistItem = {
  id: number;
  assetId: number;
  title: string;
};

export type VenuePlaylist = {
  id: number;
  name: string;
  venueName: string;
  screeningDate: string | null;
  items: VenuePlaylistItem[];
  createdAt: string | null;
  updatedAt: string | null;
  licenseCount: number;
  activeLicenseCount: number;
};

export type CreateVenuePlaylistPayload = {
  name: string;
  venueName: string;
  screeningDate?: string;
  assetIds?: number[];
};

export type OfflineLicense = {
  licId: string;
  videoId: number;
  deviceId: string;
  expiresUtc: string;
  alg: string;
  kid: string;
  keyBytes: number;
  cekWrappedB64: string;
  nonceB64: string | null;
  tagB64: string | null;
  serverEphemeralSpkiB64: string | null;
};

export type ProvisionResult = {
  playlistId: number;
  expiresUtc: string | null;
  deviceCount: number;
  videoCount: number;
  licenses: OfflineLicense[];
  failures: string[];
};

/* -------------------------------------------------------------------------- */
/* Normalizers (tolerate .NET `$values` wrappers and PascalCase fields)       */
/* -------------------------------------------------------------------------- */

type Rec = Record<string, unknown>;

const asRec = (v: unknown): Rec =>
  v && typeof v === "object" ? (v as Rec) : {};

export function asList(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  const o = asRec(data);
  for (const key of ["$values", "items", "data", "devices", "Devices", "playlists", "licenses"]) {
    if (Array.isArray(o[key])) return o[key] as unknown[];
    if (o[key] && typeof o[key] === "object") return asList(o[key]);
  }
  return [];
}

const str = (v: unknown, fallback = ""): string =>
  v === undefined || v === null ? fallback : String(v);

const optStr = (v: unknown): string | null =>
  v === undefined || v === null || v === "" ? null : String(v);

const pick = (o: Rec, ...keys: string[]): unknown => {
  for (const k of keys) {
    if (o[k] !== undefined && o[k] !== null) return o[k];
  }
  return undefined;
};

export function normalizeDevice(raw: unknown): RegisteredDevice {
  const o = asRec(raw);
  return {
    deviceId: str(pick(o, "deviceId", "DeviceId", "id", "Id")),
    platform: str(pick(o, "platform", "Platform"), "Unknown"),
    publicKey: optStr(pick(o, "publicKey", "PublicKey")) ?? undefined,
    registeredAt: str(
      pick(o, "createdUtc", "CreatedUtc", "registeredAt", "RegisteredAt", "createdAt", "CreatedAt")
    ),
  };
}

export function normalizePlaylistItem(raw: unknown): VenuePlaylistItem {
  const o = asRec(raw);
  return {
    id: Number(pick(o, "id", "Id", "itemId", "ItemId") ?? 0),
    assetId: Number(pick(o, "assetId", "AssetId") ?? 0),
    title: str(pick(o, "title", "Title", "assetTitle", "AssetTitle"), "Untitled"),
  };
}

export function normalizePlaylist(raw: unknown): VenuePlaylist {
  const o = asRec(raw);
  return {
    id: Number(pick(o, "id", "Id", "playlistId", "PlaylistId") ?? 0),
    name: str(pick(o, "name", "Name")),
    venueName: str(pick(o, "venueName", "VenueName")),
    screeningDate: optStr(pick(o, "screeningDate", "ScreeningDate")),
    items: asList(pick(o, "items", "Items")).map(normalizePlaylistItem),
    createdAt: optStr(pick(o, "createdUtc", "CreatedUtc", "createdAt", "CreatedAt")),
    updatedAt: optStr(pick(o, "updatedUtc", "UpdatedUtc")),
    licenseCount: Number(pick(o, "licenseCount", "LicenseCount") ?? 0),
    activeLicenseCount: Number(pick(o, "activeLicenseCount", "ActiveLicenseCount") ?? 0),
  };
}

export function normalizeLicense(raw: unknown): OfflineLicense {
  const o = asRec(raw);
  return {
    licId: str(pick(o, "licId", "LicId")),
    videoId: Number(pick(o, "videoId", "VideoId") ?? 0),
    deviceId: str(pick(o, "deviceId", "DeviceId")),
    expiresUtc: str(pick(o, "expiresUtc", "ExpiresUtc")),
    alg: str(pick(o, "alg", "Alg")),
    kid: str(pick(o, "kid", "Kid")),
    keyBytes: Number(pick(o, "keyBytes", "KeyBytes") ?? 0),
    cekWrappedB64: str(pick(o, "cekWrappedB64", "CekWrappedB64")),
    nonceB64: optStr(pick(o, "nonceB64", "NonceB64")),
    tagB64: optStr(pick(o, "tagB64", "TagB64")),
    serverEphemeralSpkiB64: optStr(pick(o, "serverEphemeralSpkiB64", "ServerEphemeralSpkiB64")),
  };
}

export function normalizeProvisionResult(raw: unknown): ProvisionResult {
  const o = asRec(raw);
  return {
    playlistId: Number(pick(o, "playlistId", "PlaylistId") ?? 0),
    expiresUtc: optStr(pick(o, "expiresUtc", "ExpiresUtc")),
    deviceCount: Number(pick(o, "deviceCount", "DeviceCount") ?? 0),
    videoCount: Number(pick(o, "videoCount", "VideoCount") ?? 0),
    licenses: asList(pick(o, "licenses", "Licenses")).map(normalizeLicense),
    failures: asList(pick(o, "failures", "Failures")).map(String),
  };
}

/* -------------------------------------------------------------------------- */
/* Registered headsets                                                        */
/* -------------------------------------------------------------------------- */

export async function getRegisteredDevices(): Promise<RegisteredDevice[]> {
  const res = await apiClient.get("/api/devices");
  return asList(res.data).map(normalizeDevice);
}

export async function registerDevice(
  payload: RegisterDevicePayload
): Promise<RegisteredDevice> {
  const res = await apiClient.post("/api/devices/register", {
    deviceId: payload.deviceId.trim(),
    platform: payload.platform,
    appVersion: payload.appVersion.trim(),
    devicePublicKey: payload.publicKey
      .replace(/-----BEGIN PUBLIC KEY-----|-----END PUBLIC KEY-----/g, "")
      .replace(/\s/g, ""),
  });
  const body = asRec(res.data);
  return normalizeDevice(body.device ?? res.data);
}

export async function removeRegisteredDevice(deviceId: string): Promise<void> {
  await apiClient.delete(`/api/devices/${encodeURIComponent(deviceId)}`);
}

/* -------------------------------------------------------------------------- */
/* Venue playlists                                                            */
/* -------------------------------------------------------------------------- */

export async function getVenuePlaylists(): Promise<VenuePlaylist[]> {
  const res = await apiClient.get("/api/Venue/playlists");
  return asList(res.data).map(normalizePlaylist);
}

export async function createVenuePlaylist(
  payload: CreateVenuePlaylistPayload
): Promise<VenuePlaylist> {
  const res = await apiClient.post("/api/Venue/playlists", payload);
  const body = asRec(res.data);
  return normalizePlaylist(body.playlist ?? res.data);
}

export async function addVenuePlaylistItem(
  playlistId: number,
  assetId: number
): Promise<VenuePlaylistItem> {
  const res = await apiClient.post(
    `/api/Venue/playlists/${playlistId}/items`,
    { assetId }
  );
  const body = asRec(res.data);
  return normalizePlaylistItem(body.item);
}

export async function removeVenuePlaylistItem(
  playlistId: number,
  itemId: number
): Promise<void> {
  await apiClient.delete(`/api/Venue/playlists/${playlistId}/items/${itemId}`);
}

/** Mints offline screening licenses for every registered headset. */
export async function provisionVenuePlaylist(
  playlistId: number,
  days?: number
): Promise<ProvisionResult> {
  const res = await apiClient.post(
    `/api/Venue/playlists/${playlistId}/provision`,
    days === undefined ? {} : { days }
  );
  return normalizeProvisionResult(res.data);
}
